// /**
//  * ChecklistBuilderGridForm.jsx
//  * Drop-in replacement for checklistBuilderForm.js
//  *
//  * Cell type system — each cell does ONE thing:
//  *   HEADER      → bold blue section label only
//  *   DESCRIPTION → plain text / instruction only
//  *   INPUT       → response widget only (OK/Not OK, Text, Number, Options, Date, Live Image Capture)
//  *   IMAGE       → sample reference images only (no live capture here)
//  */

// import {
//   Button, Form, Input, Select, message, TreeSelect,
//   Spin, Modal, Row, Col, Tooltip, Dropdown, Tag, Switch,
// } from "antd";
// import {
//   PlusOutlined, DeleteOutlined, MergeCellsOutlined,
//   SplitCellsOutlined, PlusSquareOutlined, MinusSquareOutlined,
//   ColumnWidthOutlined, FontColorsOutlined, FormOutlined,
//   PictureOutlined, TagOutlined, CameraOutlined,
// } from "@ant-design/icons";
// import { useEffect, useRef, useState, useCallback } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import TextArea from "antd/es/input/TextArea";
// import { fetchProduct } from "../../../../../configuration/module/Product/Service/product";
// import { fetchBuilderById, postBuilder, putBuilder } from "../Service/checklistBuilder";

// const { Option } = Select;

// // ─── Constants ────────────────────────────────────────────────────────────────
// const DEFAULT_COLS  = 5;
// const DEFAULT_COL_W = 180;

// const CELL_TYPES = [
//   { value: "HEADER",      label: "Header",      icon: <TagOutlined />,        bg: "#e6f4ff", color: "#1677ff" },
//   { value: "DESCRIPTION", label: "Description", icon: <FontColorsOutlined />, bg: "#ffffff", color: "#333"    },
//   { value: "INPUT",       label: "Input",       icon: <FormOutlined />,       bg: "#fffbe6", color: "#7c5b00" },
//   { value: "IMAGE",       label: "Image",       icon: <PictureOutlined />,    bg: "#f6ffed", color: "#237804" },
// ];

// const cellTypeMeta = (type) => CELL_TYPES.find((t) => t.value === type) || CELL_TYPES[1];

// // ─── Pure helpers ─────────────────────────────────────────────────────────────
// const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// const buildTreeData = (nodes) =>
//   nodes.map((n) => ({
//     title: n.productName, value: n.uuid, key: n.uuid,
//     children: n.children ? buildTreeData(n.children) : [],
//   }));

// const fileToBase64 = (file) =>
//   new Promise((res, rej) => {
//     const r = new FileReader();
//     r.readAsDataURL(file);
//     r.onload  = () => res(r.result.split(",")[1]);
//     r.onerror = rej;
//   });

// const emptyCell = () => ({
//   id: uid(), colSpan: 1, rowSpan: 1, hidden: false,
//   cellType: "DESCRIPTION",
//   label: "",
//   description: "",
//   inputType: "STRING",
//   options: [],
//   sampleImages: [],
//   isMandatory: false,
//   analysisLabel: "",       // NEW — title for the Binding Analysis chart
//   analysisChartType: null, // NEW — LINE/HISTOGRAM (Number) or BAR/PIE (Options)
// });

// const emptyRow  = (cols) => ({ id: uid(), cells: Array.from({ length: cols }, emptyCell) });
// const emptyGrid = (rows = 4, cols = DEFAULT_COLS) =>
//   Array.from({ length: rows }, () => emptyRow(cols));

// // ── gridMeta ──────────────────────────────────────────────────────────────────
// const encodeMeta = (ri, ci, cell) =>
//   JSON.stringify({
//     ri, ci,
//     colSpan:     cell.colSpan     || 1,
//     rowSpan:     cell.rowSpan     || 1,
//     cellType:    cell.cellType    || "DESCRIPTION",
//     label:       cell.label       || "",
//     isMandatory: cell.isMandatory || false,
//     isHeader:    cell.cellType === "HEADER",
//   });

// const decodeMeta = (str) => { try { return JSON.parse(str); } catch { return null; } };

// // ── Flatten grid → flat records ───────────────────────────────────────────────
// const gridToRecords = async (rows) => {
//   const records = [];
//   let seq = 1;
//   for (let ri = 0; ri < rows.length; ri++) {
//     for (let ci = 0; ci < rows[ri].cells.length; ci++) {
//       const cell = rows[ri].cells[ci];
//       if (cell.hidden) continue;

//       const sampleImages = await Promise.all(
//         (cell.sampleImages || []).map((f) =>
//           f.originFileObj
//             ? fileToBase64(f.originFileObj)
//             : (f.url || "").replace(/^data:image\/\w+;base64,/, "")
//         )
//       );

//       const isHeader = cell.cellType === "HEADER";
//       records.push({
//         id:          cell.backendId ?? undefined,
//         description: isHeader
//           ? (cell.label || " ")
//           : cell.cellType === "DESCRIPTION"
//             ? (cell.description || "")
//             : "",
//         inputType:   isHeader ? "STRING" : (cell.inputType || "STRING"),
//         options:     cell.inputType === "OPTIONS" ? (cell.options || []) : [],
//         sequenceNo:  seq++,
//         sampleImages,
//         gridMeta:    encodeMeta(ri, ci, cell),
//         isMandatory: cell.cellType === "INPUT" ? (cell.isMandatory || false) : false,
//         // NEW — only meaningful for INPUT cells, and never for LIVE_IMAGE
//         analysisLabel: cell.cellType === "INPUT" && cell.inputType !== "LIVE_IMAGE"
//           ? (cell.analysisLabel || null)
//           : null,
//         analysisChartType: cell.cellType === "INPUT"
//           ? (cell.analysisChartType || null)
//           : null,
//       });
//     }
//   }
//   return records;
// };

// // ── Reconstruct grid from flat records ────────────────────────────────────────
// const recordsToGrid = (records, colCount) => {
//   if (!records?.length) return emptyGrid(4, colCount);

//   const hasMeta = records.some((r) => r.gridMeta);

//   if (!hasMeta) {
//     return records.map((r) => {
//       const row  = emptyRow(colCount);
//       const cell = row.cells[0];
//       Object.assign(cell, {
//         backendId:   r.id,
//         cellType:    "DESCRIPTION",
//         description: r.description,
//         inputType:   r.inputType || "STRING",
//         options:     r.options   || [],
//         colSpan:     colCount,
//         isMandatory: r.isMandatory || false,
//         analysisLabel:     r.analysisLabel     || "",   // NEW
//         analysisChartType: r.analysisChartType || null, // NEW
//         sampleImages: (r.sampleImages || []).map((img, i) => ({
//           uid: `si_${i}`, name: `image-${i + 1}.png`, status: "done",
//           url: `data:image/png;base64,${img}`, originFileObj: null,
//         })),
//       });
//       for (let ci = 1; ci < colCount; ci++) row.cells[ci].hidden = true;
//       return row;
//     });
//   }

//   const maxRi = Math.max(...records.map((r) => decodeMeta(r.gridMeta)?.ri ?? 0)) + 1;
//   const grid  = emptyGrid(maxRi, colCount);

//   records.forEach((r) => {
//     const meta = decodeMeta(r.gridMeta);
//     if (!meta) return;
//     const cell = grid[meta.ri]?.cells[meta.ci];
//     if (!cell) return;

//     const cellType = meta.cellType || (meta.isHeader ? "HEADER" : "DESCRIPTION");

//     Object.assign(cell, {
//       backendId:   r.id,
//       cellType,
//       description: r.description,
//       inputType:   r.inputType  || "STRING",
//       options:     r.options    || [],
//       colSpan:     meta.colSpan || 1,
//       rowSpan:     meta.rowSpan || 1,
//       label:       meta.label   || (cellType === "HEADER" ? r.description : ""),
//       isMandatory: r.isMandatory || meta.isMandatory || false,
//       analysisLabel:     r.analysisLabel     || "",   // NEW
//       analysisChartType: r.analysisChartType || null, // NEW
//       sampleImages: (r.sampleImages || []).map((img, i) => ({
//         uid: `si_${i}`, name: `image-${i + 1}.png`, status: "done",
//         url: `data:image/png;base64,${img}`, originFileObj: null,
//       })),
//     });

//     for (let dr = 0; dr < (meta.rowSpan || 1); dr++) {
//       for (let dc = 0; dc < (meta.colSpan || 1); dc++) {
//         if (dr === 0 && dc === 0) continue;
//         const covered = grid[meta.ri + dr]?.cells[meta.ci + dc];
//         if (covered) covered.hidden = true;
//       }
//     }
//   });

//   return grid;
// };

// // ─── Main Component ───────────────────────────────────────────────────────────
// const ChecklistBuilderGridForm = ({ mode }) => {
//   const { uuid } = useParams();
//   const [form]          = Form.useForm();
//   const [rows, setRows] = useState(() => emptyGrid());
//   const [colCount, setColCount]   = useState(DEFAULT_COLS);
//   const [colWidths, setColWidths] = useState(() => Array(DEFAULT_COLS).fill(DEFAULT_COL_W));
//   const [colHeaders, setColHeaders] = useState(() =>
//     Array.from({ length: DEFAULT_COLS }, (_, i) => String.fromCharCode(65 + i))
//   );
//   const [products,  setProducts]  = useState([]);
//   const [editData,  setEditData]  = useState(null);
//   const [isLoading, setIsLoading] = useState(false);
//   const [previewSrc, setPreviewSrc] = useState(null);
//   const [activeCellForImg, setActiveCellForImg] = useState(null);
//   const fileInputRef = useRef(null);
//   const [messageApi, contextHolder] = message.useMessage();
//   const navigate = useNavigate();

//   useEffect(() => {
//     let c = false;
//     (async () => {
//       setIsLoading(true);
//       try {
//         const res = await fetchProduct();
//         if (!c) setProducts(buildTreeData(res.data));
//       } catch { message.error("Failed to load products"); }
//       finally { if (!c) setIsLoading(false); }
//     })();
//     return () => { c = true; };
//   }, []);

//   useEffect(() => {
//     if (!uuid) return;
//     let c = false;
//     (async () => {
//       setIsLoading(true);
//       try {
//         const res = await fetchBuilderById(uuid);
//         if (!c) setEditData(res.data.data);
//       } catch { message.error("Failed to load checklist"); }
//       finally { if (!c) setIsLoading(false); }
//     })();
//     return () => { c = true; };
//   }, [uuid]);

//   useEffect(() => {
//     if (editData) {
//       form.setFieldsValue({ product: editData.product?.uuid, checklistName: editData.checklistName });
//       // Compute exact column count from saved records — do NOT enforce DEFAULT_COLS minimum
//       // here, otherwise a 2-column checklist opens with 5 columns on view/edit.
//       const recordCols = (editData.records || []).map((r) => {
//         const m = decodeMeta(r.gridMeta);
//         return m ? (m.ci + (m.colSpan || 1)) : 1;
//       });
//       const savedCols = recordCols.length > 0 ? Math.max(...recordCols) : DEFAULT_COLS;
//       setColCount(savedCols);
//       setColWidths(Array(savedCols).fill(DEFAULT_COL_W));
//       setColHeaders(Array.from({ length: savedCols }, (_, i) => String.fromCharCode(65 + i)));
//       setRows(recordsToGrid(editData.records || [], savedCols));
//     } else {
//       form.resetFields();
//       setRows(emptyGrid(4, colCount));
//     }
//   }, [editData]);

//   const patchCell = useCallback((ri, ci, patch) => {
//     setRows((prev) => prev.map((row, rIdx) => rIdx !== ri ? row : {
//       ...row,
//       cells: row.cells.map((c, cIdx) =>
//         cIdx !== ci ? c : { ...c, ...(typeof patch === "function" ? patch(c) : patch) }
//       ),
//     }));
//   }, []);

//   const addRowAfter = useCallback((ri) => {
//     setRows((prev) => { const n = [...prev]; n.splice(ri + 1, 0, emptyRow(colCount)); return n; });
//   }, [colCount]);

//   const deleteRow = useCallback((ri) => {
//     setRows((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== ri));
//   }, []);

//   const addColumn = useCallback(() => {
//     const nc = colCount + 1;
//     setColCount(nc);
//     setColWidths((w) => [...w, DEFAULT_COL_W]);
//     setColHeaders((h) => [...h, String.fromCharCode(65 + h.length)]);
//     setRows((prev) => prev.map((row) => ({ ...row, cells: [...row.cells, emptyCell()] })));
//   }, [colCount]);

//   const deleteColumn = useCallback((ci) => {
//     if (colCount <= 1) return;
//     setColCount((c) => c - 1);
//     setColWidths((w) => w.filter((_, i) => i !== ci));
//     setColHeaders((h) => h.filter((_, i) => i !== ci));
//     setRows((prev) => prev.map((row) => ({ ...row, cells: row.cells.filter((_, i) => i !== ci) })));
//   }, [colCount]);

//   const mergeRight = useCallback((ri, ci) => {
//     setRows((prev) => {
//       const cell   = prev[ri]?.cells[ci];
//       const nextCi = ci + (cell?.colSpan || 1);
//       if (!prev[ri]?.cells[nextCi] || prev[ri].cells[nextCi].hidden) {
//         message.warning("No cell to the right"); return prev;
//       }
//       return prev.map((row, rIdx) => rIdx !== ri ? row : {
//         ...row,
//         cells: row.cells.map((c, cIdx) => {
//           if (cIdx === ci)     return { ...c, colSpan: (c.colSpan || 1) + 1 };
//           if (cIdx === nextCi) return { ...c, hidden: true };
//           return c;
//         }),
//       });
//     });
//   }, []);

//   const mergeDown = useCallback((ri, ci) => {
//     setRows((prev) => {
//       const cell   = prev[ri]?.cells[ci];
//       const nextRi = ri + (cell?.rowSpan || 1);
//       if (!prev[nextRi]?.cells[ci]) { message.warning("No row below"); return prev; }
//       return prev.map((row, rIdx) => ({
//         ...row,
//         cells: row.cells.map((c, cIdx) => {
//           if (rIdx === ri     && cIdx === ci) return { ...c, rowSpan: (c.rowSpan || 1) + 1 };
//           if (rIdx === nextRi && cIdx === ci) return { ...c, hidden: true };
//           return c;
//         }),
//       }));
//     });
//   }, []);

//   const splitCell = useCallback((ri, ci) => {
//     setRows((prev) => {
//       const cell = prev[ri]?.cells[ci];
//       if (!cell) return prev;
//       const oldCS = cell.colSpan || 1, oldRS = cell.rowSpan || 1;
//       return prev.map((row, rIdx) => ({
//         ...row,
//         cells: row.cells.map((c, cIdx) => {
//           if (rIdx === ri && cIdx === ci) return { ...c, colSpan: 1, rowSpan: 1 };
//           const covC = cIdx > ci && cIdx < ci + oldCS && rIdx >= ri && rIdx < ri + oldRS;
//           const covR = rIdx > ri && rIdx < ri + oldRS && cIdx >= ci && cIdx < ci + oldCS;
//           if (covC || covR) return { ...c, hidden: false, colSpan: 1, rowSpan: 1 };
//           return c;
//         }),
//       }));
//     });
//   }, []);

//   const handleImageFiles = (e, ri, ci) => {
//     const added = [];
//     for (const f of Array.from(e.target.files)) {
//       if (!f.type.startsWith("image/")) { message.error(`${f.name} is not an image`); continue; }
//       if (f.size > 5 * 1024 * 1024)    { message.error(`${f.name} exceeds 5 MB`);     continue; }
//       added.push({ uid: uid(), name: f.name, status: "done", url: URL.createObjectURL(f), originFileObj: f });
//     }
//     if (added.length) patchCell(ri, ci, (c) => ({ sampleImages: [...(c.sampleImages || []), ...added] }));
//     e.target.value = "";
//   };

//   const ctxMenu = (ri, ci) => {
//     const cell = rows[ri]?.cells[ci];
//     return {
//       items: [
//         { key: "mr", label: "Merge right →",  icon: <MergeCellsOutlined />,                                        onClick: () => mergeRight(ri, ci) },
//         { key: "md", label: "Merge down ↓",   icon: <MergeCellsOutlined style={{ transform: "rotate(90deg)" }} />, onClick: () => mergeDown(ri, ci) },
//         { key: "sp", label: "Split cell",      icon: <SplitCellsOutlined />, disabled: cell?.colSpan === 1 && cell?.rowSpan === 1, onClick: () => splitCell(ri, ci) },
//         { type: "divider" },
//         { key: "ar", label: "Insert row below", icon: <PlusSquareOutlined />,  onClick: () => addRowAfter(ri) },
//         { key: "dr", label: "Delete row",       icon: <MinusSquareOutlined />, danger: true, onClick: () => deleteRow(ri) },
//       ],
//     };
//   };

//   const onFinish = async (values) => {
//     const dataCells = rows.flatMap((r) => r.cells)
//       .filter((c) => !c.hidden && c.cellType !== "HEADER");
//     if (!dataCells.length) { message.error("Please add at least one data cell."); return; }

//     setIsLoading(true);
//     try {
//       const flatRecords = await gridToRecords(rows);
//       const payload = {
//         checklistName: values.checklistName,
//         productId:     values.product,
//         listOfCheckListBuilderRecord: flatRecords,
//       };
//       if (editData) { await putBuilder(payload, editData.uuid); }
//       else          { await postBuilder(payload); }
//       messageApi.open({ type: "success", content: `Checklist ${editData ? "updated" : "created"} successfully` });
//       navigate({ pathname: "/check-list-builder/configuration/checklist-builder" }, { replace: true });
//     } catch {
//       messageApi.open({ type: "error", content: "Error saving checklist" });
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <Spin spinning={isLoading}>
//       {contextHolder}

//       <input
//         ref={fileInputRef} type="file" accept="image/*" multiple
//         style={{ display: "none" }}
//         onChange={(e) => {
//           if (activeCellForImg) {
//             handleImageFiles(e, activeCellForImg.ri, activeCellForImg.ci);
//             setActiveCellForImg(null);
//           }
//         }}
//       />

//       <Modal open={!!previewSrc} footer={null} onCancel={() => setPreviewSrc(null)} title="Image Preview">
//         <img src={previewSrc} alt="preview" style={{ width: "100%" }} />
//       </Modal>

//       <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 12 }}>Checklist Builder</div>

//       <Form form={form} layout="vertical" onFinish={onFinish}>
//         <Row gutter={16} style={{ marginBottom: 12 }}>
//           <Col span={6}>
//             <Form.Item name="product" label="Select Product" rules={[{ required: true }]}>
//               <TreeSelect treeData={products} placeholder="Select product" allowClear disabled={mode === "view"} />
//             </Form.Item>
//           </Col>
//           <Col span={6}>
//             <Form.Item name="checklistName" label="Checklist Name" rules={[{ required: true }]}>
//               <Input disabled={mode === "view"} />
//             </Form.Item>
//           </Col>
//           {mode !== "view" && (
//             <Col span={12}>
//               <div style={{ display: "flex", gap: 8, paddingTop: 30, flexWrap: "wrap" }}>
//                 <Button icon={<PlusSquareOutlined />} onClick={() => addRowAfter(rows.length - 1)}>Add Row</Button>
//                 <Button icon={<ColumnWidthOutlined />} onClick={addColumn}>Add Column</Button>
//                 <Button icon={<DeleteOutlined />} danger disabled={colCount <= 1} onClick={() => deleteColumn(colCount - 1)}>
//                   Remove Last Col
//                 </Button>
//               </div>
//             </Col>
//           )}
//         </Row>

//         <div style={{ overflowX: "auto", border: "1px solid #d9d9d9", borderRadius: 6 }}>
//           <table style={{
//             borderCollapse: "collapse", tableLayout: "fixed",
//             width: colWidths.reduce((a, b) => a + b, 0) + 52, minWidth: "100%",
//           }}>
//             <thead>
//               <tr>
//                 <th style={thGutter} />
//                 {colHeaders.map((h, ci) => (
//                   <th key={ci} style={{ ...thCol, width: colWidths[ci] }}>
//                     <span>{h}</span>
//                     {mode !== "view" && (
//                       <Tooltip title={`Delete column ${h}`}>
//                         <Button type="text" danger size="small" icon={<DeleteOutlined />}
//                           style={{ position: "absolute", top: 1, right: 1, padding: 0, height: 18, width: 18, fontSize: 9 }}
//                           onClick={() => deleteColumn(ci)} />
//                       </Tooltip>
//                     )}
//                   </th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {rows.map((row, ri) => {
//                 const visible = row.cells.map((c, ci) => ({ c, ci })).filter(({ c }) => !c.hidden);
//                 return (
//                   <tr key={row.id}>
//                     <td style={tdGutter}>
//                       <span style={{ display: "block", color: "#bbb", fontSize: 10 }}>{ri + 1}</span>
//                       {mode !== "view" && (
//                         <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 2 }}>
//                           <Tooltip title="Insert row below">
//                             <Button type="text" size="small" icon={<PlusOutlined />}
//                               style={{ padding: 0, height: 16, width: 20, fontSize: 10, color: "#52c41a" }}
//                               onClick={() => addRowAfter(ri)} />
//                           </Tooltip>
//                           <Tooltip title="Delete row">
//                             <Button type="text" danger size="small" icon={<DeleteOutlined />}
//                               style={{ padding: 0, height: 16, width: 20, fontSize: 10 }}
//                               onClick={() => deleteRow(ri)} />
//                           </Tooltip>
//                         </div>
//                       )}
//                     </td>
//                     {visible.map(({ c: cell, ci }) => (
//                       <GridCell
//                         key={cell.id}
//                         cell={cell} ri={ri} ci={ci} mode={mode}
//                         colWidth={colWidths[ci]}
//                         onChange={(p) => patchCell(ri, ci, p)}
//                         onMergeRight={() => mergeRight(ri, ci)}
//                         onMergeDown={() => mergeDown(ri, ci)}
//                         onSplit={() => splitCell(ri, ci)}
//                         onAddImage={() => { setActiveCellForImg({ ri, ci }); fileInputRef.current?.click(); }}
//                         onRemoveImage={(idx) => patchCell(ri, ci, (c) => ({
//                           sampleImages: c.sampleImages.filter((_, i) => i !== idx),
//                         }))}
//                         onPreview={setPreviewSrc}
//                         contextMenu={mode !== "view" ? ctxMenu(ri, ci) : { items: [] }}
//                       />
//                     ))}
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>

//         <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
//           <Button onClick={() => navigate({ pathname: "/check-list-builder/configuration/checklist-builder" }, { replace: true })}>
//             Cancel
//           </Button>
//           {mode !== "view" && <Button type="primary" htmlType="submit">Submit</Button>}
//         </div>
//       </Form>
//     </Spin>
//   );
// };

// const thGutter = { width: 52, background: "#f5f5f5", border: "1px solid #e0e0e0", padding: 4, textAlign: "center" };
// const thCol    = { background: "#f0f0f0", border: "1px solid #e0e0e0", padding: "4px 8px", fontSize: 11, fontWeight: 600, textAlign: "center", position: "relative" };
// const tdGutter = { width: 52, background: "#fafafa", border: "1px solid #e0e0e0", textAlign: "center", verticalAlign: "top", padding: "6px 2px" };
// const iconBtn  = { padding: 0, height: 20, width: 22 };

// // ─── GridCell ─────────────────────────────────────────────────────────────────
// const GridCell = ({
//   cell, ri, ci, mode, colWidth,
//   onChange, onMergeRight, onMergeDown, onSplit,
//   onAddImage, onRemoveImage, onPreview, contextMenu,
// }) => {
//   const [hover,   setHover]   = useState(false);
//   const [focused, setFocused] = useState(false);
//   const meta = cellTypeMeta(cell.cellType);

//   const tdStyle = {
//     border: `1px solid ${focused ? "#1677ff" : "#d9d9d9"}`,
//     verticalAlign: "top",
//     background: meta.bg,
//     position: "relative",
//     minHeight: 48,
//     width: colWidth,
//     padding: "6px 8px",
//     cursor: mode !== "view" ? "cell" : "default",
//     transition: "border-color 0.15s",
//     boxSizing: "border-box",
//   };

//   return (
//     <Dropdown menu={contextMenu} trigger={mode !== "view" ? ["contextMenu"] : []}>
//       <td
//         colSpan={cell.colSpan || 1}
//         rowSpan={cell.rowSpan || 1}
//         style={tdStyle}
//         onClick={() => setFocused(true)}
//         onBlur={() => setFocused(false)}
//         onMouseEnter={() => setHover(true)}
//         onMouseLeave={() => setHover(false)}
//       >
//         {mode !== "view" && hover && (
//           <div
//             style={{
//               position: "absolute", top: 2, right: 2, zIndex: 10,
//               display: "flex", gap: 1,
//               background: "rgba(255,255,255,0.97)",
//               borderRadius: 4, padding: "2px 3px",
//               boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
//             }}
//             onMouseDown={(e) => e.stopPropagation()}
//           >
//             {CELL_TYPES.map((t) => (
//               <Tooltip key={t.value} title={t.label}>
//                 <Button
//                   type="text" size="small" icon={t.icon}
//                   style={{
//                     ...iconBtn,
//                     color: cell.cellType === t.value ? t.color : "#aaa",
//                     background: cell.cellType === t.value ? t.bg : "transparent",
//                     borderRadius: 3,
//                   }}
//                   onClick={() => onChange({ cellType: t.value })}
//                 />
//               </Tooltip>
//             ))}
//             <span style={{ width: 1, background: "#e0e0e0", margin: "2px 3px" }} />
//             <Tooltip title="Merge right →">
//               <Button type="text" size="small" icon={<MergeCellsOutlined />} style={iconBtn} onClick={onMergeRight} />
//             </Tooltip>
//             <Tooltip title="Merge down ↓">
//               <Button type="text" size="small" icon={<MergeCellsOutlined style={{ transform: "rotate(90deg)" }} />} style={iconBtn} onClick={onMergeDown} />
//             </Tooltip>
//             {(cell.colSpan > 1 || cell.rowSpan > 1) && (
//               <Tooltip title="Split">
//                 <Button type="text" size="small" icon={<SplitCellsOutlined />} style={iconBtn} onClick={onSplit} />
//               </Tooltip>
//             )}
//           </div>
//         )}

//         {/* Cell type badge — always visible so view mode shows what each cell is */}
//         <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 5 }}>
//           <span style={{
//             fontSize: 10, fontWeight: 500, padding: "1px 6px",
//             borderRadius: 3, background: meta.bg,
//             color: meta.color, border: `1px solid ${meta.color}33`,
//             display: "inline-flex", alignItems: "center", gap: 3,
//             opacity: mode === "view" ? 0.7 : 1,
//           }}>
//             {meta.icon} {meta.label}
//           </span>
//         </div>

//         <CellContent
//           cell={cell} mode={mode}
//           onChange={onChange}
//           onAddImage={onAddImage}
//           onRemoveImage={onRemoveImage}
//           onPreview={onPreview}
//         />
//       </td>
//     </Dropdown>
//   );
// };

// // ─── CellContent ─────────────────────────────────────────────────────────────
// const CellContent = ({ cell, mode, onChange, onAddImage, onRemoveImage, onPreview }) => {
//   switch (cell.cellType) {

//     case "HEADER":
//       return (
//         <Input
//           value={cell.label}
//           disabled={mode === "view"}
//           placeholder="Section header label…"
//           style={{ fontWeight: 700, fontSize: 13, color: "#1677ff", border: "none", background: "transparent", padding: 0 }}
//           onChange={(e) => onChange({ label: e.target.value })}
//         />
//       );

//     case "DESCRIPTION":
//       return (
//         <TextArea
//           autoSize={{ minRows: 2, maxRows: 8 }}
//           value={cell.description}
//           disabled={mode === "view"}
//           placeholder="Enter description or instruction…"
//           style={{ fontSize: 12, border: mode === "view" ? "none" : undefined, background: "transparent" }}
//           onChange={(e) => onChange({ description: e.target.value })}
//         />
//       );

//     case "INPUT":
//       return (
//         <div>
//           {/* Input type selector */}
//           {mode !== "view" ? (
//             <Select
//               size="small"
//               value={cell.inputType}
//               style={{ width: "100%", marginBottom: 6 }}
//               onChange={(val) => onChange({ inputType: val, options: val !== "OPTIONS" ? [] : cell.options })}
//             >
//               <Option value="STRING">Text</Option>
//               <Option value="DOUBLE">Number</Option>
//               <Option value="DATE">Date</Option>
//               <Option value="OK_NOT_OK">OK / Not OK</Option>
//               <Option value="OPTIONS">Options</Option>
//               <Option value="LIVE_IMAGE">Live Image Capture</Option>
//             </Select>
//           ) : (
//             <span style={{ fontSize: 10, color: "#888", display: "block", marginBottom: 4 }}>
//               {cell.inputType === "STRING"      ? "Text"
//                : cell.inputType === "DOUBLE"    ? "Number"
//                : cell.inputType === "DATE"      ? "Date"
//                : cell.inputType === "OK_NOT_OK" ? "OK / Not OK"
//                : cell.inputType === "LIVE_IMAGE" ? "Live Image Capture"
//                : "Options"}
//             </span>
//           )}

//           {/* Mandatory / Optional toggle */}
//           <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
//             {mode !== "view" ? (
//               <>
//                 <Switch
//                   size="small"
//                   checked={cell.isMandatory || false}
//                   onChange={(val) => onChange({ isMandatory: val })}
//                 />
//                 <span style={{ fontSize: 10, color: cell.isMandatory ? "#d46b08" : "#999", fontWeight: cell.isMandatory ? 600 : 400 }}>
//                   {cell.isMandatory ? "Mandatory" : "Optional"}
//                 </span>
//               </>
//             ) : (
//               <span style={{ fontSize: 10, color: cell.isMandatory ? "#d46b08" : "#999", fontWeight: cell.isMandatory ? 600 : 400 }}>
//                 {cell.isMandatory ? "Mandatory" : "Optional"}
//               </span>
//             )}
//           </div>

//           {/* Analysis config (NEW) — captured at template-build time so the
//               Binding Analysis screen knows what title + chart to use.
//               Not shown for LIVE_IMAGE — trend charts don't apply to images. */}
//           {cell.inputType !== "LIVE_IMAGE" && (
//             <div style={{
//               marginTop: 6, paddingTop: 6, borderTop: "1px dashed #d9d9d9",
//             }}>
//               <div style={{ fontSize: 10, color: "#888", marginBottom: 4, fontWeight: 600 }}>
//                 📊 Analysis (Binding → Analysis screen)
//               </div>

//               {mode !== "view" ? (
//                 <Input
//                   size="small"
//                   value={cell.analysisLabel}
//                   placeholder="Chart title, e.g. 'Torque (Nm)' — leave blank to exclude from analysis"
//                   style={{ marginBottom: 6 }}
//                   onChange={(e) => onChange({ analysisLabel: e.target.value })}
//                 />
//               ) : (
//                 cell.analysisLabel && (
//                   <div style={{ fontSize: 11, color: "#555", marginBottom: 6 }}>
//                     Title: <strong>{cell.analysisLabel}</strong>
//                   </div>
//                 )
//               )}

//               {/* Chart type only matters for DOUBLE and OPTIONS.
//                   OK_NOT_OK / STRING / DATE have a fixed presentation, no choice needed. */}
//               {(cell.inputType === "DOUBLE" || cell.inputType === "OPTIONS") && cell.analysisLabel && (
//                 mode !== "view" ? (
//                   <Select
//                     size="small"
//                     style={{ width: "100%" }}
//                     value={cell.analysisChartType || undefined}
//                     placeholder="Chart type"
//                     onChange={(val) => onChange({ analysisChartType: val })}
//                   >
//                     {cell.inputType === "DOUBLE" && (
//                       <>
//                         <Option value="LINE">Line (trend over units)</Option>
//                         <Option value="HISTOGRAM">Histogram (distribution)</Option>
//                       </>
//                     )}
//                     {cell.inputType === "OPTIONS" && (
//                       <>
//                         <Option value="BAR">Bar (frequency)</Option>
//                         <Option value="PIE">Pie (proportion)</Option>
//                       </>
//                     )}
//                   </Select>
//                 ) : (
//                   cell.analysisChartType && (
//                     <span style={{ fontSize: 10, color: "#888" }}>Chart: {cell.analysisChartType}</span>
//                   )
//                 )
//               )}

//               {cell.inputType === "OK_NOT_OK" && cell.analysisLabel && (
//                 <span style={{ fontSize: 10, color: "#888" }}>
//                   Shown as Pass/Fail pie + pass-rate trend
//                 </span>
//               )}
//             </div>
//           )}

//           {/* LIVE_IMAGE note */}
//           {cell.inputType === "LIVE_IMAGE" && (
//             <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 4, background: "#fff7e6", border: "1px dashed #ffa940", fontSize: 11, color: "#874d00" }}>
//               <CameraOutlined style={{ fontSize: 12 }} />
//               <span>Camera capture — shown at execution time</span>
//             </div>
//           )}

//           {/* OPTIONS editor */}
//           {cell.inputType === "OPTIONS" && mode !== "view" && (
//             <div>
//               {(cell.options || []).map((opt, i) => (
//                 <Tag key={i} closable color="blue" style={{ marginBottom: 3 }}
//                   onClose={() => onChange({ options: cell.options.filter((_, j) => j !== i) })}>
//                   {opt}
//                 </Tag>
//               ))}
//               <Input.Search
//                 size="small" placeholder="Add option" enterButton="+"
//                 style={{ marginTop: 4 }}
//                 onSearch={(val) => {
//                   const v = val.trim();
//                   if (!v || (cell.options || []).includes(v)) return;
//                   onChange({ options: [...(cell.options || []), v] });
//                 }}
//               />
//             </div>
//           )}
//           {cell.inputType === "OPTIONS" && mode === "view" && (
//             <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
//               {(cell.options || []).map((o, i) => <Tag key={i}>{o}</Tag>)}
//             </div>
//           )}
//         </div>
//       );

//     case "IMAGE":
//       return (
//         <div>
//           {(cell.sampleImages?.length > 0 || mode !== "view") && (
//             <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
//               {(cell.sampleImages || []).map((img, i) => (
//                 <div key={img.uid || i} style={{ position: "relative" }}>
//                   <img
//                     src={img.url} alt="sample"
//                     style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: "1px solid #e0e0e0" }}
//                     onClick={() => onPreview(img.url)}
//                   />
//                   {mode !== "view" && (
//                     <Button danger type="text" size="small"
//                       style={{ position: "absolute", top: -4, right: -4, padding: 0, height: 16, width: 16, fontSize: 9, borderRadius: "50%", background: "#fff", border: "1px solid #f5222d" }}
//                       onClick={() => onRemoveImage(i)}>×</Button>
//                   )}
//                 </div>
//               ))}
//             </div>
//           )}
//           {mode !== "view" && (
//             <Button type="dashed" size="small" icon={<PictureOutlined />} style={{ fontSize: 11 }} onClick={onAddImage}>
//               Add Sample Image
//             </Button>
//           )}
//           {mode === "view" && !cell.sampleImages?.length && (
//             <span style={{ fontSize: 11, color: "#bbb" }}>No sample images</span>
//           )}
//         </div>
//       );

//     default:
//       return null;
//   }
// };

// export default ChecklistBuilderGridForm;

/**
 * ChecklistBuilderGridForm.jsx
 * Drop-in replacement for checklistBuilderForm.js
 *
 * Cell type system — each cell does ONE thing:
 *   HEADER      → bold blue section label only
 *   DESCRIPTION → plain text / instruction only
 *   INPUT       → response widget only (OK/Not OK, Text, Number, Options, Date, Live Image Capture)
 *   IMAGE       → sample reference images only (no live capture here)
 */

import {
  Button, Form, Input, Select, message, TreeSelect,
  Spin, Modal, Row, Col, Tooltip, Dropdown, Tag, Switch,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, MergeCellsOutlined,
  SplitCellsOutlined, PlusSquareOutlined, MinusSquareOutlined,
  ColumnWidthOutlined, FontColorsOutlined, FormOutlined,
  PictureOutlined, TagOutlined, CameraOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
import { fetchProduct } from "../../../../../configuration/module/Product/Service/product";
import { fetchBuilderById, postBuilder, putBuilder } from "../Service/checklistBuilder";

const { Option } = Select;

// ─── Constants ────────────────────────────────────────────────────────────────
const DEFAULT_COLS  = 5;
const DEFAULT_COL_W = 180;

const CELL_TYPES = [
  { value: "HEADER",      label: "Header",      icon: <TagOutlined />,        bg: "#e6f4ff", color: "#1677ff" },
  { value: "DESCRIPTION", label: "Description", icon: <FontColorsOutlined />, bg: "#ffffff", color: "#333"    },
  { value: "INPUT",       label: "Input",       icon: <FormOutlined />,       bg: "#fffbe6", color: "#7c5b00" },
  { value: "IMAGE",       label: "Image",       icon: <PictureOutlined />,    bg: "#f6ffed", color: "#237804" },
];

const cellTypeMeta = (type) => CELL_TYPES.find((t) => t.value === type) || CELL_TYPES[1];

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const buildTreeData = (nodes) =>
  nodes.map((n) => ({
    title: n.productName, value: n.uuid, key: n.uuid,
    children: n.children ? buildTreeData(n.children) : [],
  }));

const fileToBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.readAsDataURL(file);
    r.onload  = () => res(r.result.split(",")[1]);
    r.onerror = rej;
  });

const emptyCell = () => ({
  id: uid(), colSpan: 1, rowSpan: 1, hidden: false,
  cellType: "DESCRIPTION",
  label: "",
  description: "",
  inputType: "STRING",
  options: [],
  sampleImages: [],
  isMandatory: false,
  isAnalysisRequired: false, // NEW — Yes/No toggle, shown before the label
  analysisLabel: "",       // NEW — title for the Binding Analysis chart
  analysisChartType: null, // NEW — LINE/HISTOGRAM (Number) or BAR/PIE (Options)
});

const emptyRow  = (cols) => ({ id: uid(), cells: Array.from({ length: cols }, emptyCell) });
const emptyGrid = (rows = 4, cols = DEFAULT_COLS) =>
  Array.from({ length: rows }, () => emptyRow(cols));

// ── gridMeta ──────────────────────────────────────────────────────────────────
const encodeMeta = (ri, ci, cell) =>
  JSON.stringify({
    ri, ci,
    colSpan:     cell.colSpan     || 1,
    rowSpan:     cell.rowSpan     || 1,
    cellType:    cell.cellType    || "DESCRIPTION",
    label:       cell.label       || "",
    isMandatory: cell.isMandatory || false,
    isAnalysisRequired: cell.isAnalysisRequired || false,
    isHeader:    cell.cellType === "HEADER",
  });

const decodeMeta = (str) => { try { return JSON.parse(str); } catch { return null; } };

// ── Flatten grid → flat records ───────────────────────────────────────────────
const gridToRecords = async (rows) => {
  const records = [];
  let seq = 1;
  for (let ri = 0; ri < rows.length; ri++) {
    for (let ci = 0; ci < rows[ri].cells.length; ci++) {
      const cell = rows[ri].cells[ci];
      if (cell.hidden) continue;

      const sampleImages = await Promise.all(
        (cell.sampleImages || []).map((f) =>
          f.originFileObj
            ? fileToBase64(f.originFileObj)
            : (f.url || "").replace(/^data:image\/\w+;base64,/, "")
        )
      );

      const isHeader = cell.cellType === "HEADER";
      records.push({
        id:          cell.backendId ?? undefined,
        description: isHeader
          ? (cell.label || " ")
          : cell.cellType === "DESCRIPTION"
            ? (cell.description || "")
            : "",
        inputType:   isHeader ? "STRING" : (cell.inputType || "STRING"),
        options:     cell.inputType === "OPTIONS" ? (cell.options || []) : [],
        sequenceNo:  seq++,
        sampleImages,
        gridMeta:    encodeMeta(ri, ci, cell),
        isMandatory: cell.cellType === "INPUT" ? (cell.isMandatory || false) : false,
        // NEW — only sent when the "Needed for Analysis" toggle is Yes.
        // If it's No, we send null even if text was typed earlier, so the
        // backend's existing "analysisLabel non-blank" check auto-excludes it.
        analysisLabel: cell.cellType === "INPUT" && cell.inputType !== "LIVE_IMAGE" && cell.isAnalysisRequired
          ? (cell.analysisLabel || null)
          : null,
        analysisChartType: cell.cellType === "INPUT" && cell.isAnalysisRequired
          ? (cell.analysisChartType || null)
          : null,
      });
    }
  }
  return records;
};

// ── Reconstruct grid from flat records ────────────────────────────────────────
const recordsToGrid = (records, colCount) => {
  if (!records?.length) return emptyGrid(4, colCount);

  const hasMeta = records.some((r) => r.gridMeta);

  if (!hasMeta) {
    return records.map((r) => {
      const row  = emptyRow(colCount);
      const cell = row.cells[0];
      Object.assign(cell, {
        backendId:   r.id,
        cellType:    "DESCRIPTION",
        description: r.description,
        inputType:   r.inputType || "STRING",
        options:     r.options   || [],
        colSpan:     colCount,
        isMandatory: r.isMandatory || false,
        // Old records saved before this toggle existed have no isAnalysisRequired
        // flag — fall back to "was a label already set?" so nothing silently
        // disappears from Analysis on first load after this change.
        isAnalysisRequired: !!(r.analysisLabel && r.analysisLabel.trim()),
        analysisLabel:     r.analysisLabel     || "",   // NEW
        analysisChartType: r.analysisChartType || null, // NEW
        sampleImages: (r.sampleImages || []).map((img, i) => ({
          uid: `si_${i}`, name: `image-${i + 1}.png`, status: "done",
          url: `data:image/png;base64,${img}`, originFileObj: null,
        })),
      });
      for (let ci = 1; ci < colCount; ci++) row.cells[ci].hidden = true;
      return row;
    });
  }

  const maxRi = Math.max(...records.map((r) => decodeMeta(r.gridMeta)?.ri ?? 0)) + 1;
  const grid  = emptyGrid(maxRi, colCount);

  records.forEach((r) => {
    const meta = decodeMeta(r.gridMeta);
    if (!meta) return;
    const cell = grid[meta.ri]?.cells[meta.ci];
    if (!cell) return;

    const cellType = meta.cellType || (meta.isHeader ? "HEADER" : "DESCRIPTION");

    Object.assign(cell, {
      backendId:   r.id,
      cellType,
      description: r.description,
      inputType:   r.inputType  || "STRING",
      options:     r.options    || [],
      colSpan:     meta.colSpan || 1,
      rowSpan:     meta.rowSpan || 1,
      label:       meta.label   || (cellType === "HEADER" ? r.description : ""),
      isMandatory: r.isMandatory || meta.isMandatory || false,
      isAnalysisRequired: meta.isAnalysisRequired ?? !!(r.analysisLabel && r.analysisLabel.trim()),
      analysisLabel:     r.analysisLabel     || "",   // NEW
      analysisChartType: r.analysisChartType || null, // NEW
      sampleImages: (r.sampleImages || []).map((img, i) => ({
        uid: `si_${i}`, name: `image-${i + 1}.png`, status: "done",
        url: `data:image/png;base64,${img}`, originFileObj: null,
      })),
    });

    for (let dr = 0; dr < (meta.rowSpan || 1); dr++) {
      for (let dc = 0; dc < (meta.colSpan || 1); dc++) {
        if (dr === 0 && dc === 0) continue;
        const covered = grid[meta.ri + dr]?.cells[meta.ci + dc];
        if (covered) covered.hidden = true;
      }
    }
  });

  return grid;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ChecklistBuilderGridForm = ({ mode }) => {
  const { uuid } = useParams();
  const [form]          = Form.useForm();
  const [rows, setRows] = useState(() => emptyGrid());
  const [colCount, setColCount]   = useState(DEFAULT_COLS);
  const [colWidths, setColWidths] = useState(() => Array(DEFAULT_COLS).fill(DEFAULT_COL_W));
  const [colHeaders, setColHeaders] = useState(() =>
    Array.from({ length: DEFAULT_COLS }, (_, i) => String.fromCharCode(65 + i))
  );
  const [products,  setProducts]  = useState([]);
  const [editData,  setEditData]  = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [activeCellForImg, setActiveCellForImg] = useState(null);
  const fileInputRef = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  useEffect(() => {
    let c = false;
    (async () => {
      setIsLoading(true);
      try {
        const res = await fetchProduct();
        if (!c) setProducts(buildTreeData(res.data));
      } catch { message.error("Failed to load products"); }
      finally { if (!c) setIsLoading(false); }
    })();
    return () => { c = true; };
  }, []);

  useEffect(() => {
    if (!uuid) return;
    let c = false;
    (async () => {
      setIsLoading(true);
      try {
        const res = await fetchBuilderById(uuid);
        if (!c) setEditData(res.data.data);
      } catch { message.error("Failed to load checklist"); }
      finally { if (!c) setIsLoading(false); }
    })();
    return () => { c = true; };
  }, [uuid]);

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({ product: editData.product?.uuid, checklistName: editData.checklistName });
      // Compute exact column count from saved records — do NOT enforce DEFAULT_COLS minimum
      // here, otherwise a 2-column checklist opens with 5 columns on view/edit.
      const recordCols = (editData.records || []).map((r) => {
        const m = decodeMeta(r.gridMeta);
        return m ? (m.ci + (m.colSpan || 1)) : 1;
      });
      const savedCols = recordCols.length > 0 ? Math.max(...recordCols) : DEFAULT_COLS;
      setColCount(savedCols);
      setColWidths(Array(savedCols).fill(DEFAULT_COL_W));
      setColHeaders(Array.from({ length: savedCols }, (_, i) => String.fromCharCode(65 + i)));
      setRows(recordsToGrid(editData.records || [], savedCols));
    } else {
      form.resetFields();
      setRows(emptyGrid(4, colCount));
    }
  }, [editData]);

  const patchCell = useCallback((ri, ci, patch) => {
    setRows((prev) => prev.map((row, rIdx) => rIdx !== ri ? row : {
      ...row,
      cells: row.cells.map((c, cIdx) =>
        cIdx !== ci ? c : { ...c, ...(typeof patch === "function" ? patch(c) : patch) }
      ),
    }));
  }, []);

  const addRowAfter = useCallback((ri) => {
    setRows((prev) => { const n = [...prev]; n.splice(ri + 1, 0, emptyRow(colCount)); return n; });
  }, [colCount]);

  const deleteRow = useCallback((ri) => {
    setRows((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== ri));
  }, []);

  const addColumn = useCallback(() => {
    const nc = colCount + 1;
    setColCount(nc);
    setColWidths((w) => [...w, DEFAULT_COL_W]);
    setColHeaders((h) => [...h, String.fromCharCode(65 + h.length)]);
    setRows((prev) => prev.map((row) => ({ ...row, cells: [...row.cells, emptyCell()] })));
  }, [colCount]);

  const deleteColumn = useCallback((ci) => {
    if (colCount <= 1) return;
    setColCount((c) => c - 1);
    setColWidths((w) => w.filter((_, i) => i !== ci));
    setColHeaders((h) => h.filter((_, i) => i !== ci));
    setRows((prev) => prev.map((row) => ({ ...row, cells: row.cells.filter((_, i) => i !== ci) })));
  }, [colCount]);

  const mergeRight = useCallback((ri, ci) => {
    setRows((prev) => {
      const cell   = prev[ri]?.cells[ci];
      const nextCi = ci + (cell?.colSpan || 1);
      if (!prev[ri]?.cells[nextCi] || prev[ri].cells[nextCi].hidden) {
        message.warning("No cell to the right"); return prev;
      }
      return prev.map((row, rIdx) => rIdx !== ri ? row : {
        ...row,
        cells: row.cells.map((c, cIdx) => {
          if (cIdx === ci)     return { ...c, colSpan: (c.colSpan || 1) + 1 };
          if (cIdx === nextCi) return { ...c, hidden: true };
          return c;
        }),
      });
    });
  }, []);

  const mergeDown = useCallback((ri, ci) => {
    setRows((prev) => {
      const cell   = prev[ri]?.cells[ci];
      const nextRi = ri + (cell?.rowSpan || 1);
      if (!prev[nextRi]?.cells[ci]) { message.warning("No row below"); return prev; }
      return prev.map((row, rIdx) => ({
        ...row,
        cells: row.cells.map((c, cIdx) => {
          if (rIdx === ri     && cIdx === ci) return { ...c, rowSpan: (c.rowSpan || 1) + 1 };
          if (rIdx === nextRi && cIdx === ci) return { ...c, hidden: true };
          return c;
        }),
      }));
    });
  }, []);

  const splitCell = useCallback((ri, ci) => {
    setRows((prev) => {
      const cell = prev[ri]?.cells[ci];
      if (!cell) return prev;
      const oldCS = cell.colSpan || 1, oldRS = cell.rowSpan || 1;
      return prev.map((row, rIdx) => ({
        ...row,
        cells: row.cells.map((c, cIdx) => {
          if (rIdx === ri && cIdx === ci) return { ...c, colSpan: 1, rowSpan: 1 };
          const covC = cIdx > ci && cIdx < ci + oldCS && rIdx >= ri && rIdx < ri + oldRS;
          const covR = rIdx > ri && rIdx < ri + oldRS && cIdx >= ci && cIdx < ci + oldCS;
          if (covC || covR) return { ...c, hidden: false, colSpan: 1, rowSpan: 1 };
          return c;
        }),
      }));
    });
  }, []);

  const handleImageFiles = (e, ri, ci) => {
    const added = [];
    for (const f of Array.from(e.target.files)) {
      if (!f.type.startsWith("image/")) { message.error(`${f.name} is not an image`); continue; }
      if (f.size > 5 * 1024 * 1024)    { message.error(`${f.name} exceeds 5 MB`);     continue; }
      added.push({ uid: uid(), name: f.name, status: "done", url: URL.createObjectURL(f), originFileObj: f });
    }
    if (added.length) patchCell(ri, ci, (c) => ({ sampleImages: [...(c.sampleImages || []), ...added] }));
    e.target.value = "";
  };

  const ctxMenu = (ri, ci) => {
    const cell = rows[ri]?.cells[ci];
    return {
      items: [
        { key: "mr", label: "Merge right →",  icon: <MergeCellsOutlined />,                                        onClick: () => mergeRight(ri, ci) },
        { key: "md", label: "Merge down ↓",   icon: <MergeCellsOutlined style={{ transform: "rotate(90deg)" }} />, onClick: () => mergeDown(ri, ci) },
        { key: "sp", label: "Split cell",      icon: <SplitCellsOutlined />, disabled: cell?.colSpan === 1 && cell?.rowSpan === 1, onClick: () => splitCell(ri, ci) },
        { type: "divider" },
        { key: "ar", label: "Insert row below", icon: <PlusSquareOutlined />,  onClick: () => addRowAfter(ri) },
        { key: "dr", label: "Delete row",       icon: <MinusSquareOutlined />, danger: true, onClick: () => deleteRow(ri) },
      ],
    };
  };

  const onFinish = async (values) => {
    const dataCells = rows.flatMap((r) => r.cells)
      .filter((c) => !c.hidden && c.cellType !== "HEADER");
    if (!dataCells.length) { message.error("Please add at least one data cell."); return; }

    setIsLoading(true);
    try {
      const flatRecords = await gridToRecords(rows);
      const payload = {
        checklistName: values.checklistName,
        productId:     values.product,
        listOfCheckListBuilderRecord: flatRecords,
      };
      if (editData) { await putBuilder(payload, editData.uuid); }
      else          { await postBuilder(payload); }
      messageApi.open({ type: "success", content: `Checklist ${editData ? "updated" : "created"} successfully` });
      navigate({ pathname: "/check-list-builder/configuration/checklist-builder" }, { replace: true });
    } catch {
      messageApi.open({ type: "error", content: "Error saving checklist" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Spin spinning={isLoading}>
      {contextHolder}

      <input
        ref={fileInputRef} type="file" accept="image/*" multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (activeCellForImg) {
            handleImageFiles(e, activeCellForImg.ri, activeCellForImg.ci);
            setActiveCellForImg(null);
          }
        }}
      />

      <Modal open={!!previewSrc} footer={null} onCancel={() => setPreviewSrc(null)} title="Image Preview">
        <img src={previewSrc} alt="preview" style={{ width: "100%" }} />
      </Modal>

      <div style={{ fontWeight: 700, fontSize: "1rem", marginBottom: 12 }}>Checklist Builder</div>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={6}>
            <Form.Item name="product" label="Select Product" rules={[{ required: true }]}>
              <TreeSelect treeData={products} placeholder="Select product" allowClear disabled={mode === "view"} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="checklistName" label="Checklist Name" rules={[{ required: true }]}>
              <Input disabled={mode === "view"} />
            </Form.Item>
          </Col>
          {mode !== "view" && (
            <Col span={12}>
              <div style={{ display: "flex", gap: 8, paddingTop: 30, flexWrap: "wrap" }}>
                <Button icon={<PlusSquareOutlined />} onClick={() => addRowAfter(rows.length - 1)}>Add Row</Button>
                <Button icon={<ColumnWidthOutlined />} onClick={addColumn}>Add Column</Button>
                <Button icon={<DeleteOutlined />} danger disabled={colCount <= 1} onClick={() => deleteColumn(colCount - 1)}>
                  Remove Last Col
                </Button>
              </div>
            </Col>
          )}
        </Row>

        <div style={{ overflowX: "auto", border: "1px solid #d9d9d9", borderRadius: 6 }}>
          <table style={{
            borderCollapse: "collapse", tableLayout: "fixed",
            width: colWidths.reduce((a, b) => a + b, 0) + 52, minWidth: "100%",
          }}>
            <thead>
              <tr>
                <th style={thGutter} />
                {colHeaders.map((h, ci) => (
                  <th key={ci} style={{ ...thCol, width: colWidths[ci] }}>
                    <span>{h}</span>
                    {mode !== "view" && (
                      <Tooltip title={`Delete column ${h}`}>
                        <Button type="text" danger size="small" icon={<DeleteOutlined />}
                          style={{ position: "absolute", top: 1, right: 1, padding: 0, height: 18, width: 18, fontSize: 9 }}
                          onClick={() => deleteColumn(ci)} />
                      </Tooltip>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                const visible = row.cells.map((c, ci) => ({ c, ci })).filter(({ c }) => !c.hidden);
                return (
                  <tr key={row.id}>
                    <td style={tdGutter}>
                      <span style={{ display: "block", color: "#bbb", fontSize: 10 }}>{ri + 1}</span>
                      {mode !== "view" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 2 }}>
                          <Tooltip title="Insert row below">
                            <Button type="text" size="small" icon={<PlusOutlined />}
                              style={{ padding: 0, height: 16, width: 20, fontSize: 10, color: "#52c41a" }}
                              onClick={() => addRowAfter(ri)} />
                          </Tooltip>
                          <Tooltip title="Delete row">
                            <Button type="text" danger size="small" icon={<DeleteOutlined />}
                              style={{ padding: 0, height: 16, width: 20, fontSize: 10 }}
                              onClick={() => deleteRow(ri)} />
                          </Tooltip>
                        </div>
                      )}
                    </td>
                    {visible.map(({ c: cell, ci }) => (
                      <GridCell
                        key={cell.id}
                        cell={cell} ri={ri} ci={ci} mode={mode}
                        colWidth={colWidths[ci]}
                        onChange={(p) => patchCell(ri, ci, p)}
                        onMergeRight={() => mergeRight(ri, ci)}
                        onMergeDown={() => mergeDown(ri, ci)}
                        onSplit={() => splitCell(ri, ci)}
                        onAddImage={() => { setActiveCellForImg({ ri, ci }); fileInputRef.current?.click(); }}
                        onRemoveImage={(idx) => patchCell(ri, ci, (c) => ({
                          sampleImages: c.sampleImages.filter((_, i) => i !== idx),
                        }))}
                        onPreview={setPreviewSrc}
                        contextMenu={mode !== "view" ? ctxMenu(ri, ci) : { items: [] }}
                      />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={() => navigate({ pathname: "/check-list-builder/configuration/checklist-builder" }, { replace: true })}>
            Cancel
          </Button>
          {mode !== "view" && <Button type="primary" htmlType="submit">Submit</Button>}
        </div>
      </Form>
    </Spin>
  );
};

const thGutter = { width: 52, background: "#f5f5f5", border: "1px solid #e0e0e0", padding: 4, textAlign: "center" };
const thCol    = { background: "#f0f0f0", border: "1px solid #e0e0e0", padding: "4px 8px", fontSize: 11, fontWeight: 600, textAlign: "center", position: "relative" };
const tdGutter = { width: 52, background: "#fafafa", border: "1px solid #e0e0e0", textAlign: "center", verticalAlign: "top", padding: "6px 2px" };
const iconBtn  = { padding: 0, height: 20, width: 22 };

// ─── GridCell ─────────────────────────────────────────────────────────────────
const GridCell = ({
  cell, ri, ci, mode, colWidth,
  onChange, onMergeRight, onMergeDown, onSplit,
  onAddImage, onRemoveImage, onPreview, contextMenu,
}) => {
  const [hover,   setHover]   = useState(false);
  const [focused, setFocused] = useState(false);
  const meta = cellTypeMeta(cell.cellType);

  const tdStyle = {
    border: `1px solid ${focused ? "#1677ff" : "#d9d9d9"}`,
    verticalAlign: "top",
    background: meta.bg,
    position: "relative",
    minHeight: 48,
    width: colWidth,
    padding: "6px 8px",
    cursor: mode !== "view" ? "cell" : "default",
    transition: "border-color 0.15s",
    boxSizing: "border-box",
  };

  return (
    <Dropdown menu={contextMenu} trigger={mode !== "view" ? ["contextMenu"] : []}>
      <td
        colSpan={cell.colSpan || 1}
        rowSpan={cell.rowSpan || 1}
        style={tdStyle}
        onClick={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {mode !== "view" && hover && (
          <div
            style={{
              position: "absolute", top: 2, right: 2, zIndex: 10,
              display: "flex", gap: 1,
              background: "rgba(255,255,255,0.97)",
              borderRadius: 4, padding: "2px 3px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {CELL_TYPES.map((t) => (
              <Tooltip key={t.value} title={t.label}>
                <Button
                  type="text" size="small" icon={t.icon}
                  style={{
                    ...iconBtn,
                    color: cell.cellType === t.value ? t.color : "#aaa",
                    background: cell.cellType === t.value ? t.bg : "transparent",
                    borderRadius: 3,
                  }}
                  onClick={() => onChange({ cellType: t.value })}
                />
              </Tooltip>
            ))}
            <span style={{ width: 1, background: "#e0e0e0", margin: "2px 3px" }} />
            <Tooltip title="Merge right →">
              <Button type="text" size="small" icon={<MergeCellsOutlined />} style={iconBtn} onClick={onMergeRight} />
            </Tooltip>
            <Tooltip title="Merge down ↓">
              <Button type="text" size="small" icon={<MergeCellsOutlined style={{ transform: "rotate(90deg)" }} />} style={iconBtn} onClick={onMergeDown} />
            </Tooltip>
            {(cell.colSpan > 1 || cell.rowSpan > 1) && (
              <Tooltip title="Split">
                <Button type="text" size="small" icon={<SplitCellsOutlined />} style={iconBtn} onClick={onSplit} />
              </Tooltip>
            )}
          </div>
        )}

        {/* Cell type badge — always visible so view mode shows what each cell is */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 5 }}>
          <span style={{
            fontSize: 10, fontWeight: 500, padding: "1px 6px",
            borderRadius: 3, background: meta.bg,
            color: meta.color, border: `1px solid ${meta.color}33`,
            display: "inline-flex", alignItems: "center", gap: 3,
            opacity: mode === "view" ? 0.7 : 1,
          }}>
            {meta.icon} {meta.label}
          </span>
        </div>

        <CellContent
          cell={cell} mode={mode}
          onChange={onChange}
          onAddImage={onAddImage}
          onRemoveImage={onRemoveImage}
          onPreview={onPreview}
        />
      </td>
    </Dropdown>
  );
};

// ─── CellContent ─────────────────────────────────────────────────────────────
const CellContent = ({ cell, mode, onChange, onAddImage, onRemoveImage, onPreview }) => {
  switch (cell.cellType) {

    case "HEADER":
      return (
        <Input
          value={cell.label}
          disabled={mode === "view"}
          placeholder="Section header label…"
          style={{ fontWeight: 700, fontSize: 13, color: "#1677ff", border: "none", background: "transparent", padding: 0 }}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      );

    case "DESCRIPTION":
      return (
        <TextArea
          autoSize={{ minRows: 2, maxRows: 8 }}
          value={cell.description}
          disabled={mode === "view"}
          placeholder="Enter description or instruction…"
          style={{ fontSize: 12, border: mode === "view" ? "none" : undefined, background: "transparent" }}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      );

    case "INPUT":
      return (
        <div>
          {/* Input type selector */}
          {mode !== "view" ? (
            <Select
              size="small"
              value={cell.inputType}
              style={{ width: "100%", marginBottom: 6 }}
              onChange={(val) => onChange({ inputType: val, options: val !== "OPTIONS" ? [] : cell.options })}
            >
              <Option value="STRING">Text</Option>
              <Option value="DOUBLE">Number</Option>
              <Option value="DATE">Date</Option>
              <Option value="OK_NOT_OK">OK / Not OK</Option>
              <Option value="OPTIONS">Options</Option>
              <Option value="LIVE_IMAGE">Live Image Capture</Option>
            </Select>
          ) : (
            <span style={{ fontSize: 10, color: "#888", display: "block", marginBottom: 4 }}>
              {cell.inputType === "STRING"      ? "Text"
               : cell.inputType === "DOUBLE"    ? "Number"
               : cell.inputType === "DATE"      ? "Date"
               : cell.inputType === "OK_NOT_OK" ? "OK / Not OK"
               : cell.inputType === "LIVE_IMAGE" ? "Live Image Capture"
               : "Options"}
            </span>
          )}

          {/* Mandatory / Optional toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            {mode !== "view" ? (
              <>
                <Switch
                  size="small"
                  checked={cell.isMandatory || false}
                  onChange={(val) => onChange({ isMandatory: val })}
                />
                <span style={{ fontSize: 10, color: cell.isMandatory ? "#d46b08" : "#999", fontWeight: cell.isMandatory ? 600 : 400 }}>
                  {cell.isMandatory ? "Mandatory" : "Optional"}
                </span>
              </>
            ) : (
              <span style={{ fontSize: 10, color: cell.isMandatory ? "#d46b08" : "#999", fontWeight: cell.isMandatory ? 600 : 400 }}>
                {cell.isMandatory ? "Mandatory" : "Optional"}
              </span>
            )}
          </div>

          {/* Analysis config — Yes/No toggle first (same pattern as
              Mandatory/Optional). Label + chart type only appear once the
              user opts in. Turning it off clears analysisLabel so the field
              drops out of Binding → Analysis immediately (see gridToRecords).
              Not shown for LIVE_IMAGE — trend charts don't apply to images. */}
          {cell.inputType !== "LIVE_IMAGE" && (
            <div style={{
              marginTop: 6, paddingTop: 6, borderTop: "1px dashed #d9d9d9",
            }}>
              <div style={{ fontSize: 10, color: "#888", marginBottom: 4, fontWeight: 600 }}>
                📊 Analysis (Binding → Analysis screen)
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                {mode !== "view" ? (
                  <>
                    <Switch
                      size="small"
                      checked={cell.isAnalysisRequired || false}
                      onChange={(val) => onChange({
                        isAnalysisRequired: val,
                        ...(!val ? { analysisLabel: "", analysisChartType: null } : {}),
                      })}
                    />
                    <span style={{ fontSize: 10, color: cell.isAnalysisRequired ? "#1677ff" : "#999", fontWeight: cell.isAnalysisRequired ? 600 : 400 }}>
                      {cell.isAnalysisRequired ? "Needed for Analysis" : "Not Needed for Analysis"}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 10, color: cell.isAnalysisRequired ? "#1677ff" : "#999", fontWeight: cell.isAnalysisRequired ? 600 : 400 }}>
                    {cell.isAnalysisRequired ? "Needed for Analysis" : "Not Needed for Analysis"}
                  </span>
                )}
              </div>

              {cell.isAnalysisRequired && (
                <>
                  {mode !== "view" ? (
                    <Input
                      size="small"
                      value={cell.analysisLabel}
                      placeholder="Chart title, e.g. 'Torque (Nm)'"
                      style={{ marginBottom: 6 }}
                      onChange={(e) => onChange({ analysisLabel: e.target.value })}
                    />
                  ) : (
                    cell.analysisLabel && (
                      <div style={{ fontSize: 11, color: "#555", marginBottom: 6 }}>
                        Title: <strong>{cell.analysisLabel}</strong>
                      </div>
                    )
                  )}

                  {/* Chart type only matters for DOUBLE and OPTIONS.
                      OK_NOT_OK / STRING / DATE have a fixed presentation, no choice needed. */}
                  {(cell.inputType === "DOUBLE" || cell.inputType === "OPTIONS") && cell.analysisLabel && (
                    mode !== "view" ? (
                      <Select
                        size="small"
                        style={{ width: "100%" }}
                        value={cell.analysisChartType || undefined}
                        placeholder="Chart type"
                        onChange={(val) => onChange({ analysisChartType: val })}
                      >
                        {cell.inputType === "DOUBLE" && (
                          <>
                            <Option value="LINE">Line (trend over units)</Option>
                            <Option value="HISTOGRAM">Histogram (distribution)</Option>
                          </>
                        )}
                        {cell.inputType === "OPTIONS" && (
                          <>
                            <Option value="BAR">Bar (frequency)</Option>
                            <Option value="PIE">Pie (proportion)</Option>
                          </>
                        )}
                      </Select>
                    ) : (
                      cell.analysisChartType && (
                        <span style={{ fontSize: 10, color: "#888" }}>Chart: {cell.analysisChartType}</span>
                      )
                    )
                  )}

                  {cell.inputType === "OK_NOT_OK" && cell.analysisLabel && (
                    <span style={{ fontSize: 10, color: "#888" }}>
                      Shown as Pass/Fail pie + pass-rate trend
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {/* LIVE_IMAGE note */}
          {cell.inputType === "LIVE_IMAGE" && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 4, background: "#fff7e6", border: "1px dashed #ffa940", fontSize: 11, color: "#874d00" }}>
              <CameraOutlined style={{ fontSize: 12 }} />
              <span>Camera capture — shown at execution time</span>
            </div>
          )}

          {/* OPTIONS editor */}
          {cell.inputType === "OPTIONS" && mode !== "view" && (
            <div>
              {(cell.options || []).map((opt, i) => (
                <Tag key={i} closable color="blue" style={{ marginBottom: 3 }}
                  onClose={() => onChange({ options: cell.options.filter((_, j) => j !== i) })}>
                  {opt}
                </Tag>
              ))}
              <Input.Search
                size="small" placeholder="Add option" enterButton="+"
                style={{ marginTop: 4 }}
                onSearch={(val) => {
                  const v = val.trim();
                  if (!v || (cell.options || []).includes(v)) return;
                  onChange({ options: [...(cell.options || []), v] });
                }}
              />
            </div>
          )}
          {cell.inputType === "OPTIONS" && mode === "view" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {(cell.options || []).map((o, i) => <Tag key={i}>{o}</Tag>)}
            </div>
          )}
        </div>
      );

    case "IMAGE":
      return (
        <div>
          {(cell.sampleImages?.length > 0 || mode !== "view") && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
              {(cell.sampleImages || []).map((img, i) => (
                <div key={img.uid || i} style={{ position: "relative" }}>
                  <img
                    src={img.url} alt="sample"
                    style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: "1px solid #e0e0e0" }}
                    onClick={() => onPreview(img.url)}
                  />
                  {mode !== "view" && (
                    <Button danger type="text" size="small"
                      style={{ position: "absolute", top: -4, right: -4, padding: 0, height: 16, width: 16, fontSize: 9, borderRadius: "50%", background: "#fff", border: "1px solid #f5222d" }}
                      onClick={() => onRemoveImage(i)}>×</Button>
                  )}
                </div>
              ))}
            </div>
          )}
          {mode !== "view" && (
            <Button type="dashed" size="small" icon={<PictureOutlined />} style={{ fontSize: 11 }} onClick={onAddImage}>
              Add Sample Image
            </Button>
          )}
          {mode === "view" && !cell.sampleImages?.length && (
            <span style={{ fontSize: 11, color: "#bbb" }}>No sample images</span>
          )}
        </div>
      );

    default:
      return null;
  }
};

export default ChecklistBuilderGridForm;